/**
 * PortfolioReturnsTable — multi-window returns (1D..5Y) for every uploaded
 * holding on the Rebalancing page.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, BarChart3 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "../config/api";

const COLS = [
  { label: "1D", key: "ret_1d" }, { label: "5D", key: "ret_5d" },
  { label: "10D", key: "ret_10d" }, { label: "20D", key: "ret_20d" },
  { label: "50D", key: "ret_50d" }, { label: "100D", key: "ret_100d" },
  { label: "200D", key: "ret_200d" }, { label: "1Y", key: "ret_1y" },
  { label: "3Y", key: "ret_3y" }, { label: "5Y", key: "ret_5y" },
];

const cell = (v) => v == null
  ? <span className="text-slate-600">—</span>
  : <span className={v >= 0 ? "text-emerald-300" : "text-rose-300"}>{v >= 0 ? "+" : ""}{v.toFixed(2)}%</span>;

export default function PortfolioReturnsTable({ holdings }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const tickers = [...new Set((holdings || []).map(h => (h.ticker || "").toUpperCase()).filter(Boolean))];
  const tickersKey = tickers.join(",");

  const onSort = (key) => {
    if (sortKey === key) {
      if (sortDir === "desc") setSortDir("asc");
      else { setSortKey(null); setSortDir("desc"); }
    } else {
      setSortKey(key); setSortDir("desc");
    }
  };

  const sortedTickers = sortKey
    ? [...tickers].sort((a, b) => {
        if (sortKey === "ticker") {
          return sortDir === "desc" ? b.localeCompare(a) : a.localeCompare(b);
        }
        const av = sortKey === "price" ? data?.returns?.[a]?.price : data?.returns?.[a]?.[sortKey];
        const bv = sortKey === "price" ? data?.returns?.[b]?.price : data?.returns?.[b]?.[sortKey];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return sortDir === "desc" ? bv - av : av - bv;
      })
    : tickers;

  useEffect(() => {
    if (!tickers.length) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    axios.post(`${API}/daily-rebalancing/portfolio-returns`, { tickers }, { timeout: 120000 })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickersKey, reloadKey]);

  if (!tickers.length) return null;

  const missingCount = tickers.filter(t => !data?.returns?.[t]).length;

  return (
    <Card className="bg-slate-900/40 border-slate-700" data-testid="portfolio-returns-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-white flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-cyan-400" />
          Portfolio Performance by Window
          <span className="text-xs text-slate-400 font-normal">trading-day windows · {tickers.length} holdings</span>
          <div className="ml-auto flex items-center gap-2">
            {!loading && data && missingCount > 0 && (
              <span className="text-[10px] text-amber-300" data-testid="portfolio-returns-missing">
                {missingCount} not loaded — hit Refresh
              </span>
            )}
            <button
              type="button"
              onClick={() => setReloadKey(k => k + 1)}
              disabled={loading}
              data-testid="portfolio-returns-refresh"
              className="inline-flex items-center gap-1.5 rounded border border-slate-600 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-100 hover:bg-slate-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 overflow-x-auto max-h-[560px] overflow-y-auto">
        <table className="w-full text-[11px]" data-testid="portfolio-returns-table">
          <thead className="sticky top-0 bg-slate-900 z-10">
            <tr className="text-slate-500 uppercase text-[9px] tracking-wider border-b border-slate-800">
              <th
                onClick={() => onSort("ticker")}
                data-testid="portfolio-returns-sort-ticker"
                title="Sort by ticker"
                className={`text-left py-1.5 px-2 cursor-pointer select-none hover:text-white transition-colors ${sortKey === "ticker" ? "text-cyan-300 font-bold" : ""}`}
              >
                Ticker{sortKey === "ticker" ? (sortDir === "desc" ? " ▼" : " ▲") : ""}
              </th>
              <th
                onClick={() => onSort("price")}
                data-testid="portfolio-returns-sort-price"
                title="Sort by price (high → low, click again for low → high)"
                className={`text-right py-1.5 px-2 cursor-pointer select-none hover:text-white transition-colors ${sortKey === "price" ? "text-cyan-300 font-bold" : ""}`}
              >
                Price{sortKey === "price" ? (sortDir === "desc" ? " ▼" : " ▲") : ""}
              </th>
              {COLS.map(c => (
                <th
                  key={c.key}
                  onClick={() => onSort(c.key)}
                  data-testid={`portfolio-returns-sort-${c.label}`}
                  className={`text-right py-1.5 px-2 cursor-pointer select-none hover:text-white transition-colors ${
                    sortKey === c.key ? "text-cyan-300 font-bold" : ""
                  }`}
                  title={`Sort by ${c.label} (${sortKey === c.key && sortDir === "desc" ? "high → low, click for low → high" : "high → low"})`}
                >
                  {c.label}{sortKey === c.key ? (sortDir === "desc" ? " ▼" : " ▲") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedTickers.map(t => {
              const r = data?.returns?.[t];
              return (
                <tr key={t} className="border-b border-slate-900 hover:bg-slate-800/30" data-testid={`portfolio-returns-row-${t}`}>
                  <td className="py-1.5 px-2">
                    <a href={`/stock?ticker=${t}`} target="_blank" rel="noopener noreferrer"
                       className="text-cyan-300 hover:underline font-semibold font-mono">{t}</a>
                  </td>
                  <td className="text-right py-1.5 px-2 text-slate-300 font-mono">
                    {r?.price != null ? `$${r.price.toFixed(2)}` : (loading ? "…" : "—")}
                  </td>
                  {COLS.map(c => (
                    <td key={c.key} className="text-right py-1.5 px-2 font-mono font-medium">
                      {r ? cell(r[c.key]) : (loading ? <span className="text-slate-600">…</span> : <span className="text-slate-600">—</span>)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
